import { Component } from 'vue-property-decorator'
import BaseComponent from '~/models/BaseComponent'
import { adminArea, businessArea, customerArea, discountingArea, newBusinessArea, personalArea, pricingArea, processingArea, underwritingArea } from '~/router'
import { MyStore, useStore } from '~/store'

@Component
export default class StateComponent extends BaseComponent {
    protected store!: MyStore

    get profile() {
        return this.store.profile
    }

    get activeUserId() {
        return this.profile?.id ?? null
    }

    get activeAgencyId() {
        return this.profile?.activeAgency?.id ?? null
    }

    get offer() {
        return this.store.offer
    }

    get settings() {
        return this.store.settings
    }

    get isDirectMarketing() {
        return !!this.profile?.isDirectMarketing
    }

    get isVitalityAgent() {
        return !!this.profile?.isVitalityAgent
    }

    get isInternalAgent() {
        return this.isVitalityAgent || this.isDirectMarketing
    }

    get isTelemed() {
        return !!this.profile?.isTelemed
    }

    get isCommercialUnderwriter() {
        return !!this.profile?.isCommercialUnderwriter
    }

    get roles() {
        return this.profile?.roles ?? []
    }

    get isManager() {
        return this.roles.some(r => r.endsWith('Manager'))
    }

    get isChannelManager() {
        return this.roles.includes('Channel Manager')
    }

    get isRegionalManager() {
        return this.roles.includes('Regional Manager')
    }

    get isAccountManager() {
        return this.roles.includes('Account Manager')
    }

    get isCustomer() {
        return this.roles.length === 1 && this.roles.includes('Customer')
    }

    get isAdmin() {
        return this.roles.some(r => r.endsWith('Admin'))
    }

    get isSystemAdmin() {
        return this.roles.includes('System Admin')
    }

    get isVitalityAdmin() {
        return this.roles.includes('Vitality Admin')
    }

    get isProperAdmin() {
        return this.isSystemAdmin || this.isVitalityAdmin
    }

    get showInternalDetails() {
        return this.isProperAdmin || this.isInternalUser
    }

    get showInternalNotes() {
        return this.showInternalDetails || this.isPricing
    }

    get showPricingDetails() {
        return this.isSystemAdmin || this.isPricing
    }

    get isServicingAdmin() {
        return this.roles.includes('Servicing Admin')
    }

    get isClaimsAdmin() {
        return this.roles.includes('Claims Admin')
    }

    get isPricingAdmin() {
        return this.roles.includes('Pricing Admin')
    }

    get isNewBusinessAdmin() {
        return this.roles.includes('New Business Admin')
    }

    get isUnderwritingAdmin() {
        return this.roles.includes('Underwriting Admin')
    }

    get isBrokerAdmin() {
        return this.roles.includes('Broker Admin')
    }

    get isBroker() {
        return this.roles.some(r => r.startsWith('Broker'))
    }

    get isNewBusiness() {
        return this.roles.some(r => r.startsWith('New Business'))
    }

    get isUnderwriting() {
        return this.roles.some(r => r.startsWith('Underwriting'))
    }

    get isProcessing() {
        return this.roles.some(r => r.startsWith('Processing'))
    }

    get isPricing() {
        return this.roles.some(r => r.startsWith('Pricing'))
    }

    get isDiscounting() {
        return this.roles.some(r => r.startsWith('Discounting'))
    }

    get isDiscountingOrPricing() {
        return (this.isDiscounting || this.isPricing)
    }

    get showDiscountingInformation() {
        return (this.isDiscountingOrPricing || this.isCommercialUnderwriter || this.isProperAdmin)
    }

    get isMarketing() {
        return this.roles.some(r => r.startsWith('Marketing'))
    }

    get isUnderwritingOrSystemAdmin() {
        return (this.isUnderwritingAdmin || this.isSystemAdmin)
    }

    get isNewBusinessOrSystemAdmin() {
        return (this.isNewBusinessAdmin || this.isSystemAdmin)
    }

    get isProperOrServicingAdmin() {
        return (this.isProperAdmin || this.isServicingAdmin)
    }

    get isPricingOrSystemAdmin() {
        return (this.isPricingAdmin || this.isSystemAdmin)
    }

    get isInternalUser() {
        return (
            this.isProperOrServicingAdmin ||
            this.isManager ||
            this.isNewBusiness ||
            this.isUnderwriting ||
            this.isPricingAdmin ||
            this.isClaimsAdmin ||
            this.isProcessing ||
            this.isDiscounting
        )
    }

    get areas() {
        const availableAreas: string[] = []

        if (this.roles.includes('Broker Agent')) {
            availableAreas.push(personalArea)
            availableAreas.push(businessArea)
        }

        if (this.roles.includes('New Business Agent'))
            availableAreas.push(newBusinessArea)

        if (this.roles.includes('Underwriting Agent'))
            availableAreas.push(underwritingArea)

        if (this.roles.includes('Processing Agent'))
            availableAreas.push(processingArea)

        if (this.roles.includes('Discounting Agent'))
            availableAreas.push(discountingArea)

        if (this.roles.includes('Pricing Agent'))
            availableAreas.push(pricingArea)

        if (this.isCustomer)
            availableAreas.push(customerArea)

        if (this.isAdmin || this.isManager)
            availableAreas.push(adminArea)

        return availableAreas
    }

    get area() {
        return this.routePath.split('/')[1]
    }

    beforeCreate() {
        this.store = useStore()
    }
}
